import { isEmpty, template } from 'underscore';
import { TranslatedStrings } from './translatedStrings';
import { IBingAdsI18nModel } from './types';

// maps locale to a @bingads-webui/localization json file
const localeMap: any = {
  'zh-CN': 'zh-Hans',
  'zh-HK': 'zh-Hant',
  'zh-TW': 'zh-Hant',
};

export interface II18nModel extends IBingAdsI18nModel {
  getLocale(): string;
  getString(key: string, model?: Record<string, string>): string;
}

export class I18nModel implements II18nModel {
  private locale: string;

  private translationData: TranslatedStrings;

  private translationDataLocale: string;

  private bingAdsI18n?: IBingAdsI18nModel;

  private currencyCode?: string;

  constructor(translationData: TranslatedStrings, locale: string, bingAdsI18n?: IBingAdsI18nModel, currencyCode?: string) {
    this.locale = locale;
    this.translationData = translationData;
    this.translationDataLocale = this.getTranslationDataLocale();
    this.bingAdsI18n = bingAdsI18n;

    // need to support having a default currency code here because it's not set in the CCUI i18n object by default
    this.currencyCode = currencyCode;
  }

  private getTranslationDataLocale() {
    if (this.translationData[this.locale]) {
      return this.locale;
    }

    if (localeMap[this.locale]) {
      return localeMap[this.locale];
    }

    const [language] = this.locale.split('-');
    if (this.translationData[language]) {
      return language;
    }

    return '';
  }

  getLocale() {
    return this.locale;
  }

  getString(key: string, model?: Record<string, string>) {
    let str = this.translationData[this.translationDataLocale]?.[key] || key;
    if (!isEmpty(model) && str) {
      const compiled = template(str);
      str = compiled(model);
    }

    return str;
  }

  getDateFormats() {
    return this.bingAdsI18n!.getDateFormats();
  }

  formatCurrency(number: number, currency?: string, options?: any) {
    return this.bingAdsI18n!.formatCurrency(number, currency || this.currencyCode, options);
  }

  formatCivilDate(dateObject: object, options?: any) {
    return this.bingAdsI18n!.formatCivilDate(dateObject, options);
  }

  formatDate(date: Date, options?: any) {
    return this.bingAdsI18n!.formatDate(date, options);
  }

  formatDecimal(number: number, options?: any) {
    return this.bingAdsI18n!.formatDecimal(number, options);
  }

  formatDecimalToFixed(number: number, options?: any) {
    return this.bingAdsI18n!.formatDecimalToFixed(number, options);
  }

  formatPercent(number: number, options?: any): string {
    return this.bingAdsI18n!.formatPercent(number, options);
  }

  parseCivilDate(value: string, options?: any) {
    return this.bingAdsI18n!.parseCivilDate(value, options);
  }

  dateParser(options?: any) {
    return this.bingAdsI18n!.dateParser(options);
  }
}
